import { Plus } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import clsx from "clsx";
import { Button, type ButtonProps } from "../Button/Button.js";
import { Icon } from "../Icon/Icon.js";
import styles from "./AddButton.module.scss";

export interface AddButtonProps extends Omit<ButtonProps, "size" | "fullWidth"> {
  /** Leading glyph. Defaults to `Plus`; pass `null` for a label-only pill. */
  icon?: LucideIcon | null;
  /** Defaults to "Add". Keep it short — this sits in a row, not on its own line. */
  children?: ReactNode;
}

/**
 * The commit control beside a RAPID-ADD field: type a thing, press Enter or hit Add, the box clears and
 * you type the next one. The setlist's Song Bank picker, a chip add box, an invite-by-email row.
 *
 * **Why this is a component, and why it is not `<AddRow>`.** `AddRow` is the dashed "+ Add <thing>" pill
 * that appends an EMPTY row for you to fill in; this commits text that already exists. Different gesture,
 * different shape, and conflating them would give one component two contracts.
 *
 * What it owns is the two properties every hand-rolled version gets wrong:
 *
 * - **`size="field"`**, so the pill is exactly `--ac-control-h` — the height of the box it commits. The
 *   fixed rungs cannot do this: against a 28px field, `xs` is 2px out, `sm` 8px, `md` 16px. The default
 *   `md` next to a field is the version that keeps getting written, and it reads as a button that wandered
 *   in from somewhere else.
 * - **`align-self: flex-end`**, because the sibling field carries its LABEL above its box. In a row with
 *   `align-items: stretch` (or a caller that forgot `flex-end`) the button lines up with the label instead
 *   of the input and floats above the thing it acts on. Same argument that put `align-self: flex-start`
 *   inside `AddRow` — one property, always the same, always forgotten.
 *
 * **It defaults to `type="submit"`**, because the pattern is one small `<form>` around the field and this
 * button: that makes Enter-in-the-field and clicking Add the SAME handler instead of two that drift apart.
 * A call site with no form of its own passes `type="button"` and wires `onClick`.
 */
export function AddButton({ icon = Plus, children = "Add", className, type = "submit", ...rest }: AddButtonProps) {
  return (
    <Button type={type} size="field" className={clsx(styles.addButton, className)} {...rest}>
      {icon && <Icon icon={icon} size={16} />}
      {children}
    </Button>
  );
}
